import { Button } from '@tv2/ui-system/components/Button';
import { IconButton } from '@tv2/ui-system/components/IconButton';
import { AddNext } from '@tv2/ui-system/icons/next/general/AddNext';
import { CloseBasicNext } from '@tv2/ui-system/icons/next/navigation/CloseBasicNext';
import { DownArrowNext } from '@tv2/ui-system/icons/next/navigation/DownArrowNext';
import { UpArrowNext } from '@tv2/ui-system/icons/next/navigation/UpArrowNext';
import { FunctionComponent, ReactNode, useCallback, useEffect, useState, type JSX } from 'react';
import WysiwygEditor from '../components/WysiwygEditor/WysiwygEditor';

export type PromoTextDeckInput = {
  sections: Section[];
};

type Section = {
  title: string;
  text: string;
};

export function PromoTextDeckEditor({
  onSave,
  onCancel,
  deck,
}: {
  deck: PromoTextDeckInput | null;
  onSave: (deck: PromoTextDeckInput) => void;
  onCancel: () => void;
}) {
  const {
    sections,
    disableActions,
    addSection,
    removeSection,
    updateSection,
    moveSectionDown,
    moveSectionUp,
  } = useSections(deck?.sections.map(s => ({ ...s })) ?? [{ title: '', text: '' }]);

  const [error, setError] = useState<string | undefined>(undefined);

  const handleSave = useCallback(() => {
    const validSections = sections.filter(s => s.text !== '');
    if (validSections.length === 0) {
      setError('Du skal tilføje mindst en sektion');
      return;
    }
    if (validSections.length !== sections.length) {
      setError('Tekstfeltet i en sektion må ikke være tomt');
      return;
    }
    onSave({ ...deck, sections: validSections });
  }, [deck, onSave, sections]);

  return (
    <div className="text-neutral-900 p-8">
      <div className="text-headline-xs py-4">Promo-tekst deck</div>
      <div className="pt-2 divide-y divide-neutral-300">
        {sections.map((section, index) => (
          <Section
            section={section}
            index={index}
            updateSection={updateSection}
            controls={
              <SectionControls
                index={index}
                disableActions={disableActions}
                moveSectionDown={moveSectionDown}
                moveSectionUp={moveSectionUp}
                removeSection={removeSection}
              />
            }
          />
        ))}
      </div>
      <div className="flex gap-2">
        {sections.length < 6 ? (
          <Button
            data-testid="add_section_btn"
            icon={AddNext}
            onClick={() => addSection()}
            color="primary"
            size="small"
            invertIcon
          >
            Tilføj sektion
          </Button>
        ) : null}
      </div>
      <div className="mt-14 flex gap-2">
        <Button data-testid="save_btn" color="primary-green" size="medium" onClick={handleSave}>
          Gem
        </Button>
        <Button color="secondary" size="medium" onClick={() => onCancel()}>
          Annuller
        </Button>
      </div>
      {error ? <ErrorDisplay>{error}</ErrorDisplay> : null}
    </div>
  );
}

function ErrorDisplay({ children }: { children: ReactNode }) {
  return <p className="text-semantic-error-on-light pt-4">{children}</p>;
}

type SectionProps = {
  section: Section;
  index: number;
  controls: JSX.Element;
  updateSection: (index: number, title?: string, text?: string) => void;
};

function Section({ section, index, updateSection, controls }: SectionProps) {
  return (
    <div className="py-6">
      <div className="flex flex-row justify-between items-center">
        <h2 className="text-list-title-xs pt-4 pb-2">Sektion {index + 1}</h2>
        {controls}
      </div>
      <div className="py-2 text-list-title-3xs">
        <p className="pb-1">Overskrift</p>
        <input
          data-testid="editor_section_title"
          type="text"
          className="border p-1 w-full border-neutral-300"
          value={section.title}
          onChange={e => updateSection(index, e.target.value)}
        />
      </div>
      <div className="py-2">
        <p className="text-list-title-3xs pb-1">Tekst</p>
        <WysiwygEditor
          initialValue={section.text}
          onChange={text => updateSection(index, undefined, text)}
        />
      </div>
    </div>
  );
}

type SectionControlsProps = {
  index: number;
  disableActions: boolean;
  moveSectionUp: (index: number) => void;
  moveSectionDown: (index: number) => void;
  removeSection: (index: number) => void;
};

function SectionControls({
  index,
  disableActions,
  moveSectionUp,
  moveSectionDown,
  removeSection,
}: SectionControlsProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (confirmDelete) {
      setTimeout(() => {
        setConfirmDelete(false);
      }, 4000);
    }
  }, [confirmDelete]);

  return (
    <div className="flex flex-row space-x-2">
      <ControlIconButton
        onClick={() => moveSectionUp(index)}
        icon={UpArrowNext}
        disabled={disableActions}
      />
      <ControlIconButton
        onClick={() => moveSectionDown(index)}
        icon={DownArrowNext}
        disabled={disableActions}
      />
      <div>
        {confirmDelete ? (
          <button
            type="button"
            onClick={() => removeSection(index)}
            className="absolute bg-red text-white py-2 px-4 rounded-full -translate-y-12 hover:scale-105 transition "
          >
            <p>Confirm</p>
          </button>
        ) : null}
        <ControlIconButton
          onClick={() => setConfirmDelete(!confirmDelete)}
          icon={CloseBasicNext}
          disabled={disableActions}
        />
      </div>
    </div>
  );
}

type ControlIconButtonProps = {
  onClick?: () => void;
  icon: FunctionComponent<{ className?: string | undefined }>;
  disabled?: boolean | undefined;
};

function ControlIconButton({ onClick, icon, disabled }: ControlIconButtonProps) {
  return (
    <IconButton
      onClick={onClick}
      size="x-small"
      color="secondary"
      icon={icon}
      disabled={disabled}
    />
  );
}

const useSections = (initialSections: Section[]) => {
  const [sections, setSections] = useState<Section[]>(initialSections);
  const [disableActions, setDisableActions] = useState(sections.length === 1);

  useEffect(() => {
    setDisableActions(sections.length === 1);
  }, [sections.length]);

  const addSection = useCallback(() => {
    setSections([...sections, { title: '', text: '' }]);
  }, [sections]);

  const removeSection = useCallback(
    (index: number) => {
      setSections(sections.filter((_, i) => i !== index));
    },
    [sections]
  );

  const updateSection = useCallback(
    (index: number, title?: string, text?: string) => {
      setSections(
        sections.map((section, i) => {
          if (i !== index) return section;
          return { ...section, title: title ?? section.title, text: text ?? section.text };
        })
      );
    },
    [sections]
  );

  const changePosition = useCallback(
    (direction: 'up' | 'down', index: number) => {
      const newPos = direction === 'up' ? index - 1 : index + 1;
      if (newPos >= 0 && newPos < sections.length) {
        setSections(oldSections => {
          const newSections = [...oldSections];
          newSections[index] = oldSections[newPos];
          newSections[newPos] = oldSections[index];
          return newSections;
        });
      } else {
        setSections(oldSections => {
          const newSections = [...oldSections];
          newSections[0] = oldSections[oldSections.length - 1];
          newSections[oldSections.length - 1] = oldSections.at(0) ?? { title: '', text: '' };
          return newSections;
        });
      }
    },
    [sections.length]
  );

  const moveSectionUp = useCallback(
    (index: number) => changePosition('up', index),
    [changePosition]
  );
  const moveSectionDown = useCallback(
    (index: number) => changePosition('down', index),
    [changePosition]
  );

  return {
    sections,
    disableActions,
    addSection,
    removeSection,
    updateSection,
    moveSectionUp,
    moveSectionDown,
  };
};
